import React, { useState, useRef, useEffect, useCallback } from 'react';
import { supportApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  PaperPlaneRight, Sparkle, ArrowClockwise, Robot, User,
} from '@phosphor-icons/react';

const SUGGESTED = [
  'What plans do you offer?',
  'How does the 30-day trial work?',
  'Is BIL SOC 2 compliant?',
  'I want to talk to a human',
];

/**
 * Embedded chat widget for the public /support page.
 * Talks to /api/support/chat (multi-turn, session-scoped LLM).
 */
export default function SupportChatWidget() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi! I'm BIL Assistant — I can answer questions about plans, billing, security, integrations, and more. What can I help you with?",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    const optimistic = [...messages, { role: 'user', content: trimmed }];
    setMessages(optimistic);
    setInput('');
    setSending(true);
    try {
      const { data } = await supportApi.chat({
        session_id: sessionId,
        message: trimmed,
      });
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages([...optimistic, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Sorry — something went wrong. Please try again.';
      setMessages([...optimistic, { role: 'assistant', content: detail, error: true }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [messages, sessionId, sending]);

  const handleSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const reset = () => {
    setSessionId(null);
    setMessages([
      {
        role: 'assistant',
        content:
          "Fresh start. What can I help you with?",
      },
    ]);
    setInput('');
    inputRef.current?.focus();
  };

  return (
    <div
      className="bg-card border border-border rounded-sm overflow-hidden flex flex-col shadow-sm"
      style={{ height: 560 }}
      data-testid="support-chat-widget"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 border-b border-border"
        style={{ background: '#002677' }}
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-white/15 flex items-center justify-center">
            <Sparkle size={18} weight="fill" className="text-white" />
          </div>
          <div>
            <p className="font-heading text-sm font-semibold text-white tracking-tight">
              BIL Assistant
            </p>
            <p className="text-[11px] text-white/70 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00B140]" /> Online · Replies instantly
            </p>
          </div>
        </div>
        <button
          onClick={reset}
          className="p-1.5 rounded-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Reset conversation"
          data-testid="chat-reset"
        >
          <ArrowClockwise size={16} weight="bold" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#f5f6fa] dark:bg-muted/20"
        data-testid="chat-messages"
      >
        {messages.map((m, i) => (
          <MessageRow key={i} role={m.role} content={m.content} error={m.error} />
        ))}
        {sending && <TypingIndicator />}
      </div>

      {/* Suggested questions (only when conversation is fresh) */}
      {messages.length <= 1 && !sending && (
        <div className="px-5 py-3 border-t border-border bg-card flex flex-wrap gap-2" data-testid="chat-suggestions">
          {SUGGESTED.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:border-[#002677] hover:text-[#002677] transition-colors"
              data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,30)}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="px-3 py-3 border-t border-border bg-card flex items-center gap-2"
      >
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything…"
          disabled={sending}
          className="rounded-full border-border focus-visible:ring-[#002677]"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={!input.trim() || sending}
          className="rounded-full h-10 w-10 p-0 flex-shrink-0"
          style={{ background: '#002677' }}
          data-testid="chat-send"
          aria-label="Send"
        >
          <PaperPlaneRight size={16} weight="fill" className="text-white" />
        </Button>
      </form>
      <p className="px-5 pb-3 text-[10px] text-muted-foreground">
        AI-generated answers can be imperfect. For account-specific issues, ask the assistant to start a callback request.
      </p>
    </div>
  );
}

function MessageRow({ role, content, error }) {
  const isUser = role === 'user';
  return (
    <div className={`flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div
          className="h-8 w-8 rounded-sm flex items-center justify-center flex-shrink-0"
          style={{ background: '#002677' }}
        >
          <Robot size={16} weight="duotone" className="text-white" />
        </div>
      )}
      <div
        className={`rounded-sm px-4 py-2.5 max-w-[80%] text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-[#002677] text-white rounded-br-none'
            : error
              ? 'bg-[#E4002B]/10 text-[#B4001F] border border-[#E4002B]/30 rounded-bl-none'
              : 'bg-card text-foreground border border-border rounded-bl-none'
        }`}
      >
        {content}
      </div>
      {isUser && (
        <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
          <User size={16} weight="duotone" className="text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2.5 justify-start" data-testid="chat-typing">
      <div className="h-8 w-8 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: '#002677' }}>
        <Robot size={16} weight="duotone" className="text-white" />
      </div>
      <div className="rounded-sm rounded-bl-none px-4 py-3 bg-card border border-border">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '0ms' }} />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '150ms' }} />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}
